import { Button, Checkbox, Form, Input, message, Select, Spin } from "antd";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { getUserDetails, makeGetCall, makePostCall } from "../../utils/helper";
import "./add-user.scss";
import { useForm } from "antd/es/form/Form";
import CustomEditor from "../custom-editor";
import ErrorBoundary from "../../components/ErrorBoundary";
import { useModalities } from "../../hooks/useModalities";
import WorkLocationField from "../components/WorkLocationField";

const AddUser = () => {

  const [userForm] = useForm();
  const [unitsList, setUnitsList] = useState([]);
  const [permissionsList, setPermissionsList] = useState([]);
  const [rolesList, setRolesList] = useState([]);
  const [image, setImage] = useState(null);
  const [signatureHtml, setSignatureHtml] = useState(null);
  const [submitInProgress, setSubmitInProgress] = useState(false);
  const { modalityOptions, loading: modalitiesLoading } = useModalities();
  const [loadingState, setLoadingState] = useState({
    units: false,
    permissions: false,
    roles: false,
    error: null
  });

  useEffect(() => {
    getUnitsList();
    getPermissionsList();
    getRolesList();
  }, []);

  // Auto-check role-specific default permissions when the role changes.
  const handleFormValuesChange = useCallback((changedValues, allValues) => {
    if (changedValues.user_type !== undefined) {
      const currentPermissions = allValues.permissions || [];
      const additions: string[] = [];

      if (changedValues.user_type === 'technician'
          && !currentPermissions.includes('CREATE_TECH_NOTES')) {
        additions.push('CREATE_TECH_NOTES');
      }

      if ((changedValues.user_type === 'radiologist' || changedValues.user_type === 'hod')
          && !currentPermissions.includes('MANAGE_LIB_FOLDERS')) {
        additions.push('MANAGE_LIB_FOLDERS');
      }

      if (additions.length > 0) {
        userForm.setFieldsValue({
          permissions: [...currentPermissions, ...additions]
        });
      }
    }

    // Clear work location when consultant checkbox is checked
    if (changedValues.consultant !== undefined) {
      if (changedValues.consultant) {
        userForm.setFieldsValue({
          user_unit_name: []
        });
      }
    }
  }, [userForm]);

  const getUnitsList = useCallback(() => {
    setLoadingState(prev => ({ ...prev, units: true, error: null }));
    makeGetCall('/center-options')
      .then(res => {
        const units = res.data?.data || [];
        setUnitsList(units);
        // Set first option as default value for user_unit_name
        if (units.length > 0) {
          userForm.setFieldsValue({ user_unit_name: units.map(it => it.value) });
        }
        setLoadingState(prev => ({ ...prev, units: false }));
      })
      .catch(e => {
        const errorMessage = e.response?.data?.message || e.message || 'Failed to load units';
        setLoadingState(prev => ({ ...prev, units: false, error: errorMessage }));
        message.error(`Error loading units: ${errorMessage}`);
      })
  }, [userForm]);

  const getPermissionsList = useCallback(() => {
    setLoadingState(prev => ({ ...prev, permissions: true, error: null }));
    makePostCall('/permissions-list')
      .then(res => {
        setPermissionsList(res.data?.data || []);
        setLoadingState(prev => ({ ...prev, permissions: false }));
      })
      .catch(e => {
        const errorMessage = e.response?.data?.message || e.message || 'Failed to load permissions';
        setLoadingState(prev => ({ ...prev, permissions: false, error: errorMessage }));
        message.error(`Error loading permissions: ${errorMessage}`);
      })
  }, []);

  const getRolesList = useCallback(() => {
    setLoadingState(prev => ({ ...prev, roles: true, error: null }));
    makePostCall('/roles-list')
      .then(res => {
        setRolesList(res.data?.data || []);
        setLoadingState(prev => ({ ...prev, roles: false }));
      })
      .catch(e => {
        const errorMessage = e.response?.data?.message || e.message || 'Failed to load roles';
        setLoadingState(prev => ({ ...prev, roles: false, error: errorMessage }));
        message.error(`Error loading roles: ${errorMessage}`);
      })
  }, []);

  const handleImageChange = useCallback((event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        message.error('File size must be less than 5MB');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        message.error('Please select a valid image file');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result);
      };
      reader.onerror = () => {
        message.error('Failed to read the image file');
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const onSubmit = useCallback(async () => {
    try {
      // Validate form fields first - this throws if validation fails
      const values = await userForm.validateFields();

      // Type-safe destructuring after validation passes
      const {
        user_firstname,
        user_lastname,
        username,
        password,
        user_designation,
        user_mobile,
        user_email,
        user_type,
        user_unit_name,
        user_degree,
        permissions,
        user_modalities,
        consultant
      } = values;

      // Additional validation for signature based on user type
      if (user_type === 'hod' && (!signatureHtml || signatureHtml.trim() === '')) {
        message.error("Signature is required for HOD users");
        return;
      }

      // Security: Ensure all required fields are present after validation
      if (!user_firstname || !username || !password || !user_type || !user_unit_name) {
        message.error("Missing required fields. Please check the form.");
        return;
      }


      const payload = {
        user_firstname: user_firstname?.trim(),
        user_lastname: user_lastname?.trim(),
        username: username?.trim()?.toLowerCase(),
        password: password.toUpperCase(),
        user_designation: user_designation?.trim(),
        user_mobile: user_mobile?.trim(),
        user_email: user_email?.trim()?.toLowerCase(),
        user_type,
        user_unit_name,
        user_degree: user_degree?.trim(),
        permissions: permissions || [],
        um_list: user_modalities.join(','),
        user_id: getUserDetails().username,
        user_signature: signatureHtml || '',
        consultant: Boolean(consultant),
      };

      setSubmitInProgress(true);
      const res = await makePostCall('/add-user', payload);

      if (res.data?.success) {
        message.success('User added successfully');
        userForm.resetFields();
        setSignatureHtml(null);
      } else {
        const errorMessage = res.data?.message || 'Failed to add user';
        message.error(errorMessage);
      }
    } catch (validationError: any) {
      if (validationError.errorFields) {
        // Form validation errors
        message.error("Please fix the validation errors in the form");
      } else {
        // API errors
        const errorMessage = validationError.response?.data?.message || validationError.message || 'Failed to add user';
        message.error(errorMessage);
      }
    } finally {
      setSubmitInProgress(false);
    }
  }, [userForm, signatureHtml]);

  const isLoading = submitInProgress || loadingState.units || loadingState.permissions || loadingState.roles || modalitiesLoading;
  const loadingMessage = submitInProgress ? "Creating user..." : "Loading data...";

  // Memoized retry handler to prevent unnecessary re-renders
  const handleRetry = useCallback(() => {
    getUnitsList();
    getPermissionsList();
    getRolesList();
  }, [getUnitsList, getPermissionsList, getRolesList]);

  // Memoized options for better performance
  const roleOptions = useMemo(() =>
    rolesList?.map(role => ({
      label: role.role_label,
      value: role.role_code
    })) || [], [rolesList]
  );

  // const unitOptions = useMemo(() =>
  //   unitsList?.map(unit => ({
  //     label: unit.unit_label,
  //     value: unit.unit_code
  //   })) || [], [unitsList]
  // );

  const permissionCheckboxes = useMemo(() =>
    permissionsList?.map(permission => (
      <Checkbox key={permission.permission_code} value={permission.permission_code}>
        {permission.permission_label}
      </Checkbox>
    )) || [], [permissionsList]
  );

  useEffect(() => {
    if (!modalityOptions?.length) {
      return;
    }

    const currentModalities = userForm.getFieldValue('user_modalities');
    if (currentModalities?.length > 0) {
      return;
    }

    userForm.setFieldsValue({
      user_modalities: modalityOptions
        .map((option) => option?.value)
    });
  }, [modalityOptions, userForm]);

  // Memoized signature change handler with basic XSS protection
  const handleSignatureChange = useCallback((html) => {
    // Basic XSS prevention - remove script tags and event handlers
    const sanitizedHtml = html
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/\son\w+\s*=\s*["'][^"']*["']/gi, '')
      .replace(/javascript:/gi, '');

    setSignatureHtml(sanitizedHtml);
  }, []);
  // Memoized signature validator with current signatureHtml state
  const signatureValidator = useCallback((_, value) => {
    const userType = userForm.getFieldValue('user_type');
    if (userType === 'hod' && (!signatureHtml || signatureHtml.trim() === '')) {
      return Promise.reject(new Error('Please input signature!'));
    }
    return Promise.resolve();
  }, [signatureHtml, userForm]);

  return (
    <ErrorBoundary>
      <Spin spinning={isLoading} tip={loadingMessage}>
        <div className="add-user-container">
        {loadingState.error && (
          <div className="error-container">
            <strong>Error:</strong> {loadingState.error}
            <Button
              type="link"
              size="small"
              onClick={handleRetry}
              className="retry-button"
            >
              Retry
            </Button>
          </div>
        )}
        <div className="add-user-main-container admin-form-main-container">
          {/* Page Title */}
          <div className="page-header admin-form-header">
            <h1 className="page-title admin-form-title">Create New User</h1>
          </div>
          <Form
            form={userForm}
            onFinish={onSubmit}
            onValuesChange={handleFormValuesChange}
            layout="vertical"
            className="admin-modern-form"
          >
            <div className="admin-form-content-wrapper">
              {/* Left Column - Input Fields */}
              <div className="admin-form-left-column">
                {/* Account Information */}
                <div className="admin-form-section">
                  <div className="admin-form-grid-two-columns">
                    <Form.Item
                      name="username"
                      label="Username"
                      rules={[
                        {
                          required: true,
                          message: "Please input login ID!",
                        },
                        {
                          min: 3,
                          message: "Username must be at least 3 characters long!",
                        },
                        {
                          pattern: /^[a-zA-Z0-9_.]+$/,
                          message: "Username can only contain letters, numbers, underscores, and dots!",
                        },
                      ]}
                    >
                      <Input autoComplete="off" placeholder="Enter username" />
                    </Form.Item>

                    <Form.Item
                      name="user_type"
                      label="User Role"
                      rules={[
                        {
                          required: true,
                          message: "Please select user role!",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select Role"
                        options={roleOptions}
                        allowClear
                      />
                    </Form.Item>
                  </div>
                  <div className="admin-form-grid-two-columns">
                    <Form.Item
                      name="password"
                      label="Password"
                      rules={[
                        {
                          required: true,
                          message: "Please input user password!",
                        },
                        {
                          min: 4,
                          message: "Password must be at least 4 characters long!",
                        },
                      ]}
                      className="password"
                    >
                      <Input.Password autoComplete="new-password" placeholder="Enter password" />
                    </Form.Item>

                    <Form.Item
                      name="confirmPassword"
                      label="Confirm Password"
                      dependencies={['password']}
                      rules={[
                        {
                          required: true,
                          message: "Please re-type password!",
                        },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue('password') === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error('Passwords do not match!'));
                          },
                        }),
                      ]}
                    >
                      <Input.Password autoComplete="new-password" placeholder="Confirm password" />
                    </Form.Item>
                  </div>
                </div>

                {/* Personal Information */}
                <div className="admin-form-section">
                  <div className="admin-form-grid-two-columns">
                <Form.Item
                  name="user_firstname"
                  label="First Name"
                  rules={[
                    {
                      required: true,
                      message: "Please input first name!",
                    },
                    {
                      pattern: /^[a-zA-Z\s]+$/,
                      message: "First name can only contain letters and spaces!",
                    },
                    {
                      min: 2,
                      message: "First name must be at least 2 characters long!",
                    },
                  ]}
                >
                  <Input autoComplete="given-name" placeholder="Enter first name" />
                </Form.Item>

                <Form.Item
                  name="user_lastname"
                  label="Last Name"
                  rules={[
                    {
                      required: true,
                      message: "Please input last name!",
                    },
                    {
                      pattern: /^[a-zA-Z\s]*$/,
                      message: "Last name can only contain letters and spaces!",
                    },
                  ]}
                >
                  <Input autoComplete="family-name" placeholder="Enter last name" />
                </Form.Item>
              </div>

              <div className="admin-form-grid-two-columns">
                <Form.Item
                  name="user_email"
                  label="Email Address"
                  rules={[
                    {
                      type: "email",
                      message: "Please enter a valid email address!",
                    },
                  ]}
                >
                  <Input type="email" autoComplete="email" placeholder="Enter email address" />
                </Form.Item>

                <Form.Item
                  name="user_mobile"
                  label="Mobile Number"
                  rules={[
                    {
                      pattern: /^[6-9]\d{9}$/,
                      message: "Please enter a valid 10-digit mobile number starting with 6-9!",
                    },
                  ]}
                >
                  <Input autoComplete="tel" placeholder="Enter mobile number" maxLength={10} />
                </Form.Item>
                  </div>
                </div>

                {/* Professional Information */}
                <div className="admin-form-section">
                  <div className="admin-form-grid-two-columns">
                <Form.Item
                  label="Designation"
                  name="user_designation"
                  rules={[
                  ]}
                >
                  <Input placeholder="Enter designation" />
                </Form.Item>

                <Form.Item
                  label="Degree"
                  name="user_degree"
                  rules={[
                  ]}
                >
                  <Input placeholder="Enter degree" />
                </Form.Item>
                  </div>

                  {
                    unitsList && unitsList?.length >= 1 && (
                      <WorkLocationField unitsList={unitsList} form={userForm} className="" />
                    )
                  }

                  <Form.Item
                    label="Modalities"
                    name="user_modalities"
                    rules={[
                      {
                        type: 'array',
                        min: 1,
                        required: true,
                        message: 'Please select at least one modality!',
                      },
                    ]}
                  >
                    <Select
                      mode="multiple"
                      placeholder="Select modalities"
                      style={{ fontSize: 8 }}
                      options={modalityOptions}
                      allowClear
                    />
                    </Form.Item>
                  <Form.Item
                    name="consultant"
                      valuePropName="checked"

                  >
                    <Checkbox>Consultant</Checkbox>
                  </Form.Item>
                </div>
              </div>

              {/* Right Column - Signature + Permissions */}
              <div className="admin-form-right-column">
                {/* Digital Signature */}
                <div className="admin-form-section">
                  <Form.Item
                    name="user_signature"
                    label="Signature (Required for HOD users)"
                    rules={[
                      {
                        validator: signatureValidator,
                      },
                    ]}
                  >
                <div className="admin-custom-editor-wrapper">
                  <CustomEditor
                    placeholder="Enter signature"
                    initialContent={''}
                    handleChange={handleSignatureChange}
                    fromReporting={false}
                  />
                    </div>
                  </Form.Item>
                </div>

                {/* User Permissions */}
                <div className="admin-form-section">
                  <Form.Item
                    name="permissions"
                    label="User Permissions"
                  >
                    <Checkbox.Group>
                      <div className="admin-permissions-grid">
                        {permissionCheckboxes}
                      </div>
                    </Checkbox.Group>
                  </Form.Item>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="admin-form-actions-bottom">
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="admin-create-btn"
                    size="large"
                  >
                    Create User Account
                  </Button>
                </Form.Item>
            </div>
          </Form>
        </div>
        </div>
      </Spin>
    </ErrorBoundary>
  )
}


export default AddUser;
